import {
  evaluateFormula,
  resolveCustomProperties,
  resolveViewRendererLayout,
  sortViewRendererComponents,
} from '@streamlain/view-config';

import type { ViewRenderMode } from '../../types/view-component.types';
import type {
  ViewComponentInstance,
  ViewConfigSettings,
} from '../../types/view-config.types';

export interface RuntimeRenderInstance {
  key: string;
  instance: ViewComponentInstance;
  originId: string;
}

export interface RuntimeRenderPlanEntry extends RuntimeRenderInstance {
  motionIndex: number;
}

export function expandRuntimeComponentInstance(input: {
  instance: ViewComponentInstance;
  mode: ViewRenderMode;
  resolveFormulaData: (
    instance: ViewComponentInstance
  ) => Record<string, unknown>;
}): RuntimeRenderInstance[] {
  const { instance, mode, resolveFormulaData } = input;
  if (mode === 'builder') {
    return [{ key: instance.id, instance, originId: instance.id }];
  }

  const baseData = resolveFormulaData(instance);
  if (!instance.repeat) {
    return evaluateCondition(instance, baseData)
      ? [{ key: instance.id, instance, originId: instance.id }]
      : [];
  }

  const repeated = evaluateFormula(instance.repeat, { data: baseData });
  if (!Array.isArray(repeated)) {
    throw new Error(`Component "${instance.id}" repeat must return an array`);
  }

  return repeated.flatMap((item, index) => {
    const runtimeRowId = readRepeatRowId(item);
    const repeatedInstance: ViewComponentInstance = {
      ...instance,
      props: {
        ...instance.props,
        ListItem: item,
        ...(runtimeRowId ? { __slRuntimeRowId: runtimeRowId } : {}),
      },
      condition: null,
      repeat: null,
      repeatKey: null,
    };
    const itemData = {
      ...resolveFormulaData(repeatedInstance),
      ListItem: { Item: item, Index: index, Key: String(index) },
    };
    if (!evaluateCondition(instance, itemData)) return [];

    const repeatKey = instance.repeatKey
      ? evaluateFormula(instance.repeatKey, { data: itemData })
      : index;
    if (
      repeatKey !== null &&
      repeatKey !== undefined &&
      typeof repeatKey !== 'string' &&
      typeof repeatKey !== 'number'
    ) {
      throw new Error(
        `Component "${instance.id}" repeatKey must return string or number`
      );
    }
    const key = repeatKey == null ? String(index) : String(repeatKey);
    return [
      {
        key: `${instance.id}:${key}`,
        instance: { ...repeatedInstance, id: `${instance.id}:${key}` },
        originId: instance.id,
      },
    ];
  });
}

export function resolveRuntimeCustomProperties(
  instance: ViewComponentInstance,
  formulaData: Record<string, unknown>
): Record<string, string> {
  return resolveCustomProperties(instance.customProperties, {
    data: formulaData,
  });
}

export function createSharedViewRenderPlan(input: {
  components: readonly ViewComponentInstance[];
  settings: Pick<ViewConfigSettings, 'pageShape'>;
  activePageId?: string | null;
  mode: ViewRenderMode;
  resolveFormulaData: (
    instance: ViewComponentInstance,
    originId: string
  ) => Record<string, unknown>;
}) {
  const runtimeInstances = selectActiveViewComponents(
    input.components,
    input.activePageId
  ).flatMap(instance =>
    expandRuntimeComponentInstance({
      instance,
      mode: input.mode,
      resolveFormulaData: runtimeInstance =>
        input.resolveFormulaData(runtimeInstance, instance.id),
    })
  );
  const components = sortViewRendererComponents(
    runtimeInstances.map(item => item.instance)
  );
  const layout = resolveViewRendererLayout(
    components,
    input.settings.pageShape
  );
  const runtimeById = new Map(
    runtimeInstances.map(item => [item.instance.id, item] as const)
  );
  const componentSlots = Object.fromEntries(
    Object.entries(layout.componentSlots).map(([slot, instances]) => [
      slot,
      instances.map((instance, fallbackIndex) => {
        const runtime = runtimeById.get(instance.id);
        return {
          key: runtime?.key ?? instance.id,
          instance,
          originId: runtime?.originId ?? instance.id,
          motionIndex: Math.max(
            0,
            components.indexOf(instance) >= 0
              ? components.indexOf(instance)
              : fallbackIndex
          ),
        } satisfies RuntimeRenderPlanEntry;
      }),
    ])
  ) as Record<keyof typeof layout.componentSlots, RuntimeRenderPlanEntry[]>;
  return {
    components,
    componentSlots,
    effectiveShape: layout.effectiveShape,
    layoutContract: layout.layoutContract,
  };
}

export function selectActiveViewComponents(
  components: readonly ViewComponentInstance[],
  activePageId: string | null | undefined
): ViewComponentInstance[] {
  return components.filter(component =>
    belongsToActivePage(component, activePageId)
  );
}

function belongsToActivePage(
  instance: ViewComponentInstance,
  activePageId: string | null | undefined
): boolean {
  if (!activePageId) return true;
  const pageId =
    typeof instance.props['pageId'] === 'string'
      ? instance.props['pageId']
      : null;
  return !pageId || pageId === activePageId;
}

function evaluateCondition(
  instance: ViewComponentInstance,
  data: Record<string, unknown>
): boolean {
  if (!instance.condition) return true;
  const condition = evaluateFormula(instance.condition, { data });
  if (typeof condition !== 'boolean') {
    throw new Error(`Component "${instance.id}" condition must return boolean`);
  }
  return condition;
}

function readRepeatRowId(item: unknown): string | null {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const candidate = item as Record<string, unknown>;
  const rowId = candidate['id'] ?? candidate['rowId'];
  return typeof rowId === 'string' && rowId.length > 0 ? rowId : null;
}
